import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Page } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle, Spinner, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import Seo from "@/components/Seo";

// Module-level cache so we never hit the verify endpoint twice for the same token
// (StrictMode, parent re-renders, etc.). `inflight` tracks the in-flight promise.
const verifiedTokens = new Map(); // token -> { user, jwt }
const failedTokens = new Set();
const inflightVerify = new Map(); // token -> Promise

function verifyOnce(token) {
  if (verifiedTokens.has(token)) return Promise.resolve(verifiedTokens.get(token));
  if (failedTokens.has(token)) return Promise.reject(new Error("This link has already been used or is invalid."));
  if (inflightVerify.has(token)) return inflightVerify.get(token);
  const p = api.post("/auth/magic-link/verify", { token })
    .then(({ data }) => {
      verifiedTokens.set(token, { user: data.user, jwt: data.token });
      inflightVerify.delete(token);
      return { user: data.user, jwt: data.token };
    })
    .catch((err) => {
      failedTokens.add(token);
      inflightVerify.delete(token);
      throw err;
    });
  inflightVerify.set(token, p);
  return p;
}

export default function MagicLinkAuth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token");
  const cached = token ? verifiedTokens.get(token) : null;
  const [phase, setPhase] = useState(() => {
    if (!token) return "error";
    if (cached) return "set_password";
    if (token && failedTokens.has(token)) return "error";
    return "verifying";
  });
  const [error, setError] = useState(!token ? "Missing link token." : "");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token || phase !== "verifying") return;
    verifyOnce(token)
      .then(({ user, jwt }) => {
        // Server set httpOnly md_token cookie. Drop the legacy token + cached user blob.
        localStorage.removeItem("md_token");
        localStorage.removeItem("md_user");
        if (refresh) refresh().catch((e) => console.warn("[MagicLink] refresh failed:", e?.message || e));
        setPhase("set_password");
      })
      .catch((err) => {
        setError(err.response?.data?.detail || err.message || "This link is invalid or expired.");
        setPhase("error");
      });
  }, [token, phase, refresh]);

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPw !== confirmPw) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      // Use change-password with empty current (works because backend created user with "" hash)
      await api.post("/auth/change-password", { current_password: "", new_password: newPw });
      toast.success("Password set, welcome aboard 🌿");
      navigate("/professional/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <Seo title="Sign in · MindDhara" description="Complete your MindDhara account setup." />
      <section className="md-section">
        <div className="mx-auto px-6" style={{ maxWidth: 512 }} data-testid="magic-link-page">
          <div className="md-card text-center">
          {phase === "verifying" && (
            <div data-testid="magic-link-verifying">
              <Spinner size={36} weight="duotone" className="animate-spin mx-auto" style={{ color: "var(--md-primary)" }} />
              <p className="mt-4" style={{ color: "var(--md-text-soft)" }}>Verifying your link…</p>
            </div>
          )}

          {phase === "set_password" && (
            <div data-testid="magic-link-set-password">
              <CheckCircle size={36} weight="duotone" className="mx-auto" style={{ color: "var(--md-primary)" }} />
              <h1 className="font-serif mt-4">Set your password</h1>
              <p className="text-base mt-2" style={{ color: "var(--md-text-soft)" }}>
                You&apos;re signed in. Pick a password you&apos;ll remember for next time.
              </p>
              <form onSubmit={savePassword} className="space-y-3 mt-6 text-left">
                <div>
                  <label className="md-label-eyebrow block mb-1.5">New password (min 6 chars)</label>
                  <input type="password" minLength={6} required className="md-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="magic-new-pw" />
                </div>
                <div>
                  <label className="md-label-eyebrow block mb-1.5">Confirm password</label>
                  <input type="password" minLength={6} required className="md-input" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="magic-confirm-pw" />
                </div>
                <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="magic-save-pw">
                  <ShieldCheck size={14} weight="bold" /> {busy ? "Saving…" : "Set password & continue"} <ArrowRight size={14} weight="bold" />
                </button>
              </form>
            </div>
          )}

          {phase === "error" && (
            <div data-testid="magic-link-error">
              <h1 className="font-serif">Link not valid</h1>
              <p className="text-base mt-2" style={{ color: "var(--md-text-soft)" }}>{error}</p>
              <button onClick={() => navigate("/login")} className="btn-primary mt-5">Go to sign in</button>
            </div>
          )}
          </div>
        </div>
      </section>
    </Page>
  );
}
